import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, LinkSimple, MagnifyingGlassPlus, ShareNetwork } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SiteShell, useSite } from "@/components/site/SiteShell";
import { PortalLightbox } from "@/components/PortalLightbox";
import { BeforeAfter } from "@/components/site/BeforeAfter";
import { CtaBanner, Eyebrow } from "@/components/site/Blocks";
import { ArrowLink, Rule, SplitText } from "@/components/site/Motion";
import { QuoteDialog } from "@/components/site/QuoteDialog";
import { usePageHead } from "@/lib/siteContent";
import { useReveal } from "@/lib/useReveal";
import { API } from "@/lib/api";

const photo = (path, w = 0) => `${API}/public/showroom/photo/${path}${w ? `?w=${w}` : ""}`;
const monthOf = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString(undefined, { month: "long", year: "numeric" }) : "";
};
// photo-set bento: first row 7/5, then 5/7, then thirds
const SPANS = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-5", "lg:col-span-7", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4"];

const Spec = ({ label, value, testId }) =>
  value ? (
    <div className="flex items-baseline justify-between gap-6 border-b border-[color:var(--s-line)] py-3.5" data-testid={testId}>
      <dt className="s-micro !text-[10px] text-[color:var(--s-mute)]">{label}</dt>
      <dd className="text-right text-base leading-snug text-[color:var(--s-ink)]">{value}</dd>
    </div>
  ) : null;

const RelatedCard = ({ r }) => (
  <Link to={`/gallery/${r.id}`} data-testid={`project-related-${r.id}`} className="group block">
    <span className="s-fig relative block aspect-[4/3] overflow-hidden bg-[color:var(--s-line)]">
      <img src={photo(r.thumb, 900)} alt={r.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
      {r.category && <span className="s-badge s-badge-light absolute left-3 top-3">{r.category}</span>}
    </span>
    <span className="s-micro mt-4 block !text-[10px] text-[color:var(--s-mute)]">{[r.product, r.color].filter(Boolean).join(" · ") || r.material}</span>
    <span className="mt-1.5 block text-lg font-medium tracking-tight text-[color:var(--s-ink)] transition-colors duration-300 group-hover:text-[color:var(--s-gold)]">{r.title}</span>
  </Link>
);

export default function SiteProject() {
  const { id } = useParams();
  const site = useSite();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [shot, setShot] = useState(-1);
  const [quoting, setQuoting] = useState(false);

  useEffect(() => {
    setData(null);
    setError("");
    fetch(`${API}/public/gallery/${id}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(setData)
      .catch(() => setError("That project is not on the website any more."));
    window.scrollTo({ top: 0 });
  }, [id]);
  useReveal([data]);
  usePageHead({
    title: data ? data.project.title || data.project.color || "Finished project" : "",
    description: data ? data.project.description || data.project.story?.slice(0, 160) || `Fabricated and installed by ${site?.shop?.name || "our crew"}.` : "",
    suffix: site?.shop?.name,
    image: data ? photo(data.project.path, 1200) : "",
    type: "article",
  });

  // the share link is served by the API so chat apps get a proper preview card, then bounces to this page
  const shareUrl = `${API}/public/p/${id}`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied — paste it anywhere");
    } catch {
      toast.error("Could not copy the link");
    }
  };
  const share = async () => {
    const name = data ? data.project.title || data.project.color : "Our work";
    if (!navigator.share) return copyLink();
    try {
      await navigator.share({ url: shareUrl, title: name, text: data?.project.description || `${name} — see the project` });
    } catch (err) {
      if (err?.name !== "AbortError") copyLink();
    }
  };

  if (error)
    return (
      <SiteShell site={site} testId="site-project">
        <div className="flex min-h-[60vh] items-center justify-center px-6 pt-28 text-center">
          <div>
            <p className="text-sm text-[color:var(--s-mute)]" data-testid="project-missing">{error}</p>
            <ArrowLink to="/gallery" className="mt-8 justify-center">Back to the gallery</ArrowLink>
          </div>
        </div>
      </SiteShell>
    );
  if (!data)
    return (
      <SiteShell site={site} testId="site-project">
        <div className="px-6 pt-40 lg:px-12">
          <div className="mx-auto aspect-[16/9] max-w-[2560px] animate-pulse bg-[color:var(--s-line)]" />
        </div>
      </SiteShell>
    );

  const { project: p, photos, stone, related } = data;
  const title = p.title || p.color || "Finished project";
  const stoneName = stone?.color || p.color || p.product;
  const rest = photos.slice(1);
  const shots = photos.map((ph, n) => ({
    path: ph.path,
    thumb: ph.thumb,
    title,
    sub: [ph.description || p.description, photos.length > 1 ? `photo ${n + 1} of ${photos.length}` : ""].filter(Boolean).join(" · "),
  }));
  const completed = monthOf(p.created_at);
  const lookMessage = `I'd like to get this look — ${title}${stoneName ? ` in ${stoneName}` : ""}${p.category ? ` for my ${p.category.toLowerCase()}` : ""}.`;

  return (
    <SiteShell site={site} testId="site-project">
      <main className="bg-[color:var(--s-bg)] px-6 pb-24 pt-28 sm:pt-32 lg:px-12">
        <div className="mx-auto max-w-[2560px]">
          <Rule className="is-in" />
          <div className="flex flex-wrap items-center justify-between gap-4 py-5">
            <Link to="/gallery" data-testid="project-back" className="s-micro inline-flex items-center gap-2 !text-[11px] text-[color:var(--s-mute)] transition-colors duration-300 hover:text-[color:var(--s-ink)]">
              <ArrowLeft size={13} weight="bold" /> All projects
            </Link>
            <nav className="s-micro flex min-w-0 items-center gap-2 !text-[10px] text-[color:var(--s-mute)]" aria-label="Breadcrumb" data-testid="project-crumbs">
              <Link to="/gallery" className="hover:text-[color:var(--s-ink)]">Gallery</Link>
              {p.category && (
                <>
                  <span aria-hidden="true">/</span>
                  <span>{p.category}</span>
                </>
              )}
              <span aria-hidden="true">/</span>
              <span className="max-w-[14rem] truncate text-[color:var(--s-ink)]">{title}</span>
            </nav>
          </div>
          <Rule className="is-in" />

          <div className="grid gap-10 py-12 sm:py-16 lg:grid-cols-[1.4fr_1fr] lg:items-end lg:gap-16">
            <div className="s-rise">
              <Eyebrow>{[p.category, stoneName].filter(Boolean).join(" · ") || "Finished project"}</Eyebrow>
              <h1 className="s-hx mt-6 break-words text-5xl sm:text-6xl lg:text-7xl" data-testid="project-title">
                <SplitText text={title} />
              </h1>
              {p.description && (
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-[color:var(--s-mute)] sm:text-lg" data-testid="project-description">
                  {p.description}
                </p>
              )}
            </div>
            <div className="s-rise flex flex-col gap-6 lg:items-end" data-delay="1">
              <div className="flex flex-wrap items-center gap-6">
                <button type="button" data-testid="project-get-look" onClick={() => setQuoting(true)} className="s-btn w-full sm:w-auto">
                  Get this look
                </button>
                <div className="flex items-center gap-1" data-testid="project-share-row">
                  <button
                    type="button"
                    data-testid="project-share"
                    onClick={share}
                    className="inline-flex h-[3.75rem] items-center gap-2 border border-[color:var(--s-line)] px-5 text-sm text-[color:var(--s-ink)] transition-colors duration-300 hover:border-[color:var(--s-ink)]"
                  >
                    <ShareNetwork size={15} weight="bold" /> Share
                  </button>
                  <button
                    type="button"
                    data-testid="project-copy-link"
                    onClick={copyLink}
                    aria-label="Copy link"
                    title="Copy link"
                    className="inline-flex h-[3.75rem] w-[3.75rem] items-center justify-center border border-[color:var(--s-line)] text-[color:var(--s-ink)] transition-colors duration-300 hover:border-[color:var(--s-ink)]"
                  >
                    <LinkSimple size={16} weight="bold" />
                  </button>
                </div>
              </div>
              <div className="s-micro flex flex-wrap gap-x-6 gap-y-2 !text-[10px] text-[color:var(--s-mute)]" data-testid="project-facts">
                {completed && <span>Completed {completed}</span>}
                <span>{photos.length} {photos.length === 1 ? "photo" : "photos"}</span>
                {p.before && <span>Before / after</span>}
              </div>
            </div>
          </div>

          {/* hero photo */}
          <div className="s-fig relative aspect-[4/3] bg-[color:var(--s-line)] sm:aspect-[16/9]" data-testid="project-hero">
            {p.before ? (
              <BeforeAfter before={photo(p.before)} after={photo(p.path)} alt={title} className="absolute inset-0" testId="project-compare" />
            ) : (
              <button type="button" data-testid="project-hero-open" onClick={() => setShot(0)} className="group absolute inset-0 block cursor-zoom-in">
                <img src={photo(p.path, 2000)} alt={title} className="h-full w-full object-cover" />
                <span className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center bg-white/94 text-[color:var(--s-ink)] transition-colors duration-300 group-hover:bg-[color:var(--s-ink)] group-hover:text-white">
                  <MagnifyingGlassPlus size={16} weight="bold" />
                </span>
              </button>
            )}
          </div>

          {/* photo set */}
          {rest.length > 0 && (
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-12" data-testid="project-photos">
              {rest.map((ph, n) => (
                <button
                  key={ph.id}
                  type="button"
                  data-testid={`project-photo-${n + 1}`}
                  onClick={() => setShot(n + 1)}
                  className={`s-reveal group relative h-[300px] cursor-zoom-in overflow-hidden bg-[color:var(--s-line)] text-left sm:h-[380px] lg:h-[460px] ${SPANS[n % SPANS.length]}`}
                  data-delay={n % 2}
                >
                  <img src={photo(ph.path, 1600)} alt={ph.title || title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(.2,.7,.2,1)] group-hover:scale-[1.05]" />
                  {ph.description && (
                    <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1a1a18]/85 to-transparent px-5 pb-5 pt-16 text-sm text-white/90 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                      {ph.description}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* the story — the case study proper */}
          {(p.story || p.edge_profile || p.sink) && (
            <section className="mt-24 grid gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-16" data-testid="project-story">
              <div>
                <Eyebrow>The story</Eyebrow>
                <h2 className="s-hx mt-5 text-4xl sm:text-5xl">How it came together.</h2>
                <dl className="mt-10 border-t border-[color:var(--s-line)]">
                  <Spec label="Edge profile" value={p.edge_profile} testId="project-edge" />
                  <Spec label="Sink" value={p.sink} testId="project-sink" />
                  <Spec label="Stone" value={stoneName} />
                  {completed && <Spec label="Completed" value={completed} />}
                </dl>
              </div>
              <div className="space-y-6 text-base leading-relaxed text-[color:var(--s-mute)] sm:text-lg" data-testid="project-story-text">
                {(p.story || "").split(/\n\s*\n/).filter(Boolean).map((para, n) => (
                  <p key={n} className={n === 0 ? "first-letter:text-[color:var(--s-ink)] text-[color:var(--s-ink)]" : ""}>
                    {para}
                  </p>
                ))}
              </div>
            </section>
          )}

          {/* stone used + project facts */}
          <section className="mt-24 grid gap-12 lg:grid-cols-2 lg:gap-16" data-testid="project-stone">
            <div>
              <Eyebrow>Stone used</Eyebrow>
              <h2 className="s-hx mt-5 text-4xl sm:text-5xl">{stoneName ? `${stoneName}.` : "Ask us about this stone."}</h2>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-[color:var(--s-mute)]" data-testid="project-stone-note">
                {stone?.key
                  ? stone.special_order
                    ? `${stone.color} is made to order — we bring it in for your project and template as soon as it lands.`
                    : `${stone.color} is on our racks right now (${stone.qty} in stock). Come see the slab in person or hold it for 48 hours while you decide.`
                  : `We can source ${stoneName || "this stone"} or show you the closest slabs on the floor today.`}
              </p>
              <div className="mt-9 flex flex-wrap gap-6">
                <button type="button" data-testid="project-get-look-2" onClick={() => setQuoting(true)} className="s-btn">
                  Get this look
                </button>
                {stone?.key ? (
                  <Link to={`/inventory/${stone.key}`} data-testid="project-see-slab" className="s-ghost">
                    See the slab
                  </Link>
                ) : (
                  <Link to={stone?.material || p.material ? `/inventory?material=${encodeURIComponent(stone?.material || p.material)}` : "/inventory"} data-testid="project-browse" className="s-ghost">
                    Browse similar stone
                  </Link>
                )}
              </div>
              <dl className="mt-12 border-t border-[color:var(--s-line)]" data-testid="project-specs">
                <Spec label="Room" value={p.category} />
                <Spec label="Stone" value={stoneName} testId="project-spec-stone" />
                <Spec label="Material" value={stone?.material || p.material || p.product} />
                <Spec label="Brand" value={stone?.brand} />
                <Spec label="Finish" value={stone?.finish} />
                <Spec label="Thickness" value={stone?.thickness} />
                <Spec label="Edge profile" value={p.edge_profile} />
                <Spec label="Sink" value={p.sink} />
                <Spec label="Completed" value={completed} />
              </dl>
            </div>
            <div className="s-fig relative aspect-[4/5] bg-[color:var(--s-line)] lg:aspect-auto lg:min-h-[560px]">
              {stone?.photo ? (
                <>
                  <img src={photo(stone.photo, 1400)} alt={stone.color} loading="lazy" className="absolute inset-0 h-full w-full object-cover" data-testid="project-stone-photo" />
                  {stone.key && (
                    <Link
                      to={`/inventory/${stone.key}`}
                      data-testid="project-stone-open"
                      className="absolute bottom-5 left-5 right-5 flex items-center justify-between gap-4 bg-white/94 px-5 py-4 text-[color:var(--s-ink)] transition-colors duration-300 hover:bg-[color:var(--s-gold-bright)]"
                    >
                      <span className="min-w-0">
                        <span className="s-micro block !text-[10px] text-[color:var(--s-mute)]">{[stone.brand, stone.material].filter(Boolean).join(" · ")}</span>
                        <span className="block truncate text-lg font-medium tracking-tight">{stone.color}</span>
                      </span>
                      <ArrowUpRight size={18} weight="bold" className="shrink-0" />
                    </Link>
                  )}
                </>
              ) : (
                <img src={photo(p.thumb, 1400)} alt={title} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
              )}
            </div>
          </section>

          {related.length > 0 && (
            <section className="mt-28" data-testid="project-related">
              <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                  <Eyebrow>More work</Eyebrow>
                  <h2 className="s-hx mt-5 text-4xl sm:text-5xl">Other projects.</h2>
                </div>
                <ArrowLink to="/gallery" testId="project-related-all">See all our work</ArrowLink>
              </div>
              <Rule className="mt-8 is-in" />
              <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-10 lg:grid-cols-4">
                {related.map((r) => (
                  <RelatedCard key={r.id} r={r} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>

      <CtaBanner cfg={site?.config?.cta} shop={site?.shop} />

      <QuoteDialog
        open={quoting}
        onClose={() => setQuoting(false)}
        color={stoneName || ""}
        message={lookMessage}
        source="Get this look"
        title={`Get this look · ${title}`}
        testId="project-quote-dialog"
      />
      {shot >= 0 && (
        <PortalLightbox prefix="project-lightbox" shots={shots} index={shot} srcOf={(path) => photo(path)} onClose={() => setShot(-1)} />
      )}
    </SiteShell>
  );
}
